import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { PDAuthService } from 'pdauth';
import { Subscription } from 'rxjs';

import { BooleanConverter } from '../../../inputConverter';
import { SystemMessagesService } from '../../../services/system-messages.service';
import { ISystemMessage, ISystemMessageCategory, IUserSystemMessageStatus } from '../../../types';
import { BaseComponent } from '../../base.component';

const dismissedCategoryName = 'Dismissed';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-system-messages',
    styleUrls: ['../../sharedStyles/base.scss', '../../sharedStyles/telerik.scss', './system-messages.component.scss'],
    templateUrl: './system-messages.component.html'
})
export class SystemMessagesComponent extends BaseComponent {
    @Output() readonly unreadMessages: EventEmitter<number> = new EventEmitter<number>();
    @Output() readonly adminMode: EventEmitter<boolean> = new EventEmitter<boolean>();

    private _active: boolean = false;
    @Input()
    set active(value: boolean) {
        this._active = BooleanConverter(value);
        this._active ? this.activate() : this.deactivate();
    }

    categories: ISystemMessageCategory[] = [];

    private _selectedCategory: number = 0;
    get selectedCategory() {
        return this._selectedCategory;
    }
    set selectedCategory(val: number) {
        this._selectedCategory = val;
        this.cdr.detectChanges();
    }

    dismissedCategoryId: number = -1;

    userMessages: IUserSystemMessageStatus[] = [];

    get filteredUserMessages() {
        let retVal = [];
        switch (this.selectedCategory) {
            case 0:
                retVal = this.userMessages.filter(message => !message.isDismissed);
                break;
            case this.dismissedCategoryId:
                retVal = this.userMessages.filter(message => message.isDismissed);
                break;
            default:
                retVal = this.userMessages.filter(message => !message.isDismissed && message.systemMessage.systemMessageCategoryId === this.selectedCategory);
                break;
        }
        return retVal;
    }

    constructor(public _vcr: ViewContainerRef,
                private readonly systemMessagesService: SystemMessagesService,
                private readonly pdauth: PDAuthService,
                private readonly cdr: ChangeDetectorRef) {
        super(_vcr, true);
        this.activate(true);
    }

    private userSubscription: Subscription;
    private permissionsSubsciption: Subscription;
    private userId: number;

    private setUserSubscriptions(initial: boolean = false) {
        this.userSubscription = this.pdauth.getLoggedInUser().subscribe(async user => {
            if (user) {
                this.userId = user.id;
                await this.setCategories();
                this.getMessages(initial);
            }
        });

        this.permissionsSubsciption = this.pdauth.getLoggedInUserPermissions().subscribe(permissions => {
            if (permissions &&  permissions.includes('System Messages Admin')) {
                this._isAdmin = true;
                this.cdr.detectChanges();
            }
        });
    }

    private async setCategories() {
        this.categories = [{ categoryName: 'All Active', id: 0 }];
        this.categories.push(...(await this.systemMessagesService.getSystemCategories()));
        this.dismissedCategoryId = Math.max(...this.categories.map(category => category.id)) + 1;
        this.categories.push({
            categoryName: dismissedCategoryName,
            id: this.dismissedCategoryId
        });
        for (const category of this.categories) {
            if (category.categoryName.startsWith('System')) {
                category.categoryShortName = category.categoryName.replace('System ', '');
            } else {
                category.categoryShortName = category.categoryName;
            }
        }
        this.cdr.detectChanges();
    }

    private async getMessages(initial: boolean = false) {
        if (this.userId) {
            this.userMessages = (await this.systemMessagesService.getUserMessageInbox(this.userId)).filter(um => {
                let valid = !!um.systemMessage;
                const now = new Date();
                if (um.systemMessage.expirationDate && new Date(um.systemMessage.expirationDate) < now) {
                    valid = false;
                }
                if (um.systemMessage.startDate && new Date(um.systemMessage.startDate) > now) {
                    valid = false;
                }
                return valid;
            });
            for (const message of this.userMessages) {
                message.systemMessage.systemMessageCategory = this.categories.find(cat => cat.id === message.systemMessage.systemMessageCategoryId);
                if (message.systemMessage.systemMessageCategory.categoryName.toLowerCase().includes('outage')) {
                    message.isOutage = true;
                }
            }
            this.getCount();
            this.cdr.detectChanges();
            if (initial) {
                this.deactivate();
            }
        }
    }

    private getCount() {
        const unreadMessages = this.userMessages.filter(message => !message.isDismissed && !message.isRead);
        this.unreadMessages.emit(unreadMessages.length);
    }

    private async activate(initial: boolean = false) {
        this.setUserSubscriptions(initial);
    }

    private deactivate() {
        this.userSubscription.unsubscribe();
        this.permissionsSubsciption.unsubscribe();
        this.userId = null;
        this.showAdmin = false;
        this.cancel();
    }

    async dismissMessage(message: IUserSystemMessageStatus) {
        await this.systemMessagesService.markUserMessageDismissed(this.userId, message.systemMessageId);
        message.isDismissed = true;
        this.getCount();
        this.cdr.detectChanges();
    }

    toggleOpen(message: IUserSystemMessageStatus) {
        message.show = !message.show;
        this.markAsRead(message);
    }

    async markAsRead(message: IUserSystemMessageStatus) {
        if (!message.isRead) {
            await this.systemMessagesService.markUserMessageRead(this.userId, message.systemMessageId);
            message.isRead = true;
            this.getCount();
            this.cdr.detectChanges();
        }
    }

    setSelectedCategory(systemMessageCategoryId: number) {
        this.selectedCategory = systemMessageCategoryId;
    }

    // Admin
    private _isAdmin = false;
    get isAdmin() {
        return this._isAdmin;
    }

    private _showAdmin = false;
    get showAdmin() {
        return this._showAdmin;
    }
    set showAdmin(val: boolean) {
        if (this.isAdmin) {
            this._showAdmin = val;
            if (val) {
                this.loadAllMessages();
            } else {
                this.getMessages();
                this.getCount();
            }
            this.adminMode.emit(this._showAdmin);
            this.cdr.detectChanges();
        }
    }

    get dataCategories() {
        return this.categories.filter(c => c.id !== 0 && c.id !== this.dismissedCategoryId);
    }

    addEdit = false;

    defaultSort: SortDescriptor = {
        field: 'id',
        dir: 'desc'
    };
    sort: SortDescriptor[] = [this.defaultSort];

    sortChange(sort: SortDescriptor[]): void {
        this.sort = sort;
        if (!this.sort || !this.sort.length || !this.sort.filter(sd => sd.dir).length) {
            this.sort = [this.defaultSort];
        }
        this.sortMessages();
    }

    sortMessages() {
        this.allSystemMessages = orderBy(this.allSystemMessages, this.sort);
    }

    updateDateString(e: any) {
        return new Date(e);
    }

    allSystemMessages: ISystemMessage[] = [];

    currentMessage: ISystemMessage = {} as any;

    async loadAllMessages() {
        this.allSystemMessages = await this.systemMessagesService.getAllMessages();
        for (const message of this.allSystemMessages) {
            message.systemMessageCategory = this.categories.find(cat => cat.id === message.systemMessageCategoryId);
            if (message.systemMessageCategory.categoryName.startsWith('System')) {
                message.systemMessageCategory.categoryShortName = message.systemMessageCategory.categoryShortName.replace('System ', '');
            }
        }
        this.sortMessages();
        this.cdr.detectChanges();
    }

    async save() {
        if (!this.currentMessage.id) {
            await this.systemMessagesService.createSystemMessage(this.currentMessage, this.userId);
        } else {
            await this.systemMessagesService.saveSystemMessage(this.currentMessage, this.userId);
        }
        this.addEdit = false;
        this.currentMessage = null;
        await this.loadAllMessages();
    }

    cancel() {
        this.addEdit = false;
        this.currentMessage = null;
    }

    editHandler({ dataItem }: { dataItem: ISystemMessage }) {
        this.currentMessage = dataItem;
        this.addEdit = true;
    }

    newMessage() {
        this.currentMessage = {
            message: '',
            startDate: new Date(),
            title: ''
        } as any;
        this.addEdit = true;
    }

    async removeHandler({ dataItem }: { dataItem: ISystemMessage }) {
        await this.systemMessagesService.deleteSystemMessage(dataItem.id);
        this.allSystemMessages.splice(this.allSystemMessages.indexOf(dataItem), 1);
    }
}
